// client/src/pages/AddEmployee.jsx
import React, { useState } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, CircularProgress,
    Alert, Divider, Table, TableBody, TableCell, TableContainer, TableRow,
    FormControl, InputLabel, Select, MenuItem, Avatar
} from '@mui/material';
import {
    BadgeOutlined, BusinessCenterOutlined, AccountBalanceOutlined,
    AssignmentTurnedInOutlined, CloudUploadOutlined
} from '@mui/icons-material';
import { employeeService } from '../services/employeeService';

const steps = ['Identity & Contacts', 'Deployment & Operations', 'Financials & Assets', 'Review & Commit'];

const AddEmployee = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [imagePreview, setImagePreview] = useState(null);
    const [formData, setFormData] = useState({
        employee_id: '', full_name: '', dob: '', email: '', phone_number: '',
        home_address: '', aadhar_number_encrypted: '', pan_number: '', job_role: '',
        system_role: 'BDE',
        reporting_manager: '', office_location: '', date_of_joining: '', date_of_exit: '',
        previous_experience_years: '', education_qualification: '', bank_name: '',
        account_number: '', ifsc_code: '', salary_details: '', corp_sim_number: '',
        laptop_serial_number: '', profile_pic_url: ''
    });

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Create local object URL for instant rendering preview
            setImagePreview(URL.createObjectURL(file));

            // Read file data to convert into string/link format for database payload
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, profile_pic_url: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setStatus({ type: '', message: '' });

        try {
            const cleanText = (val) => (val && val.trim() !== '' ? val.trim() : null);

            const payload = {
                id: cleanText(formData.employee_id),
                name: cleanText(formData.full_name),
                email: cleanText(formData.email),
                phone: cleanText(formData.phone_number),
                job_role: cleanText(formData.job_role),
                reporting_manager: cleanText(formData.reporting_manager),
                office_location: cleanText(formData.office_location),
                home_address: cleanText(formData.home_address),
                aadhar_number_encrypted: cleanText(formData.aadhar_number_encrypted),
                pan_number: cleanText(formData.pan_number),
                education_qualification: cleanText(formData.education_qualification),
                bank_name: cleanText(formData.bank_name),
                account_number: cleanText(formData.account_number),
                ifsc_code: cleanText(formData.ifsc_code),
                corp_sim_number: cleanText(formData.corp_sim_number),
                laptop_serial_number: cleanText(formData.laptop_serial_number),
                profile_pic_url: formData.profile_pic_url, // Contains the converted link payload

                password: "Technglobal@123", // Default password for new employee accounts
                role: formData.system_role,

                previous_experience_years: formData.previous_experience_years && formData.previous_experience_years.trim() !== ''
                    ? parseFloat(formData.previous_experience_years) : null,
                salary_details: formData.salary_details && formData.salary_details.trim() !== ''
                    ? parseFloat(formData.salary_details) : null,

                dob: cleanText(formData.dob),
                date_of_joining: cleanText(formData.date_of_joining),
                date_of_exit: cleanText(formData.date_of_exit),
            };

            await employeeService.createEmployee(payload);

            setStatus({ type: 'success', message: 'Success: Employee file saved to system records.' });
            setActiveStep(0);
            setImagePreview(null);
            setFormData({
                employee_id: '', full_name: '', dob: '', email: '', phone_number: '',
                home_address: '', aadhar_number_encrypted: '', pan_number: '', job_role: '',
                system_role: 'BDE', reporting_manager: '', office_location: '', date_of_joining: '',
                date_of_exit: '', previous_experience_years: '', education_qualification: '',
                bank_name: '', account_number: '', ifsc_code: '', salary_details: '',
                corp_sim_number: '', laptop_serial_number: '', profile_pic_url: ''
            });
        } catch (err) {
            console.error(err);
            setStatus({
                type: 'error',
                message: err.response?.data?.message || 'Infrastructure or validation failure occurred.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ bgcolor: '#f3f3f3', minHeight: '100vh', color: '#181818', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>

            {/* FIXED HEADER (HEIGHT: 55px) */}
            <Box sx={{
                bgcolor: '#c3af5675',
                height: '55px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                color: '#ffffff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                zIndex: 1100
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ bgcolor: '#0176d3', p: 0.5, borderRadius: '4px', display: 'flex' }}>
                        <BusinessCenterOutlined sx={{ color: '#fff', fontSize: '20px' }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#081c3b' }}>
                        New Employee Workspace
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" sx={{ color: '#0176d3', borderColor: '#c9c9c9', textTransform: 'none', borderRadius: '4px', fontWeight: 600 }}>
                        Save as Draft
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button variant="contained" size="small" onClick={handleSubmit} disabled={loading} sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none', borderRadius: '4px', fontWeight: 600, px: 2 }}>
                            {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm & Submit'}
                        </Button>
                    ) : (
                        <Button variant="contained" size="small" onClick={handleNext} disabled={activeStep === 0 && (!formData.employee_id || !formData.full_name || !formData.email)} sx={{ bgcolor: '#0176d3', '&:hover': { bgcolor: '#014496' }, textTransform: 'none', borderRadius: '4px', fontWeight: 600, px: 2 }}>
                            Next Stage
                        </Button>
                    )}
                </Box>
            </Box>

            {/* MAIN LAYOUT MARGIN FROM FIXED HEADER */}
            <Box sx={{ pt: '80px', pb: 5, px: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>

                {/* SALESFORCE PATH / STEPPER COMPONENT */}
                <Box sx={{ display: 'flex', width: '100%', mb: 3, overflowX: 'auto', pb: 1 }}>
                    {steps.map((label, index) => {
                        const isActive = index === activeStep;
                        const isCompleted = index < activeStep;
                        return (
                            <Box key={label} sx={{
                                flex: 1, minWidth: '160px', p: 1, textAlign: 'center',
                                borderBottom: isActive ? '4px solid #0176d3' : isCompleted ? '4px solid #2e7d32' : '4px solid #e0e0e0',
                                bgcolor: isActive ? '#eef4ff' : 'transparent',
                                transition: 'all 0.2s ease'
                            }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: isCompleted ? '#2e7d32' : '#515151' }}>
                                    STAGE {index + 1}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: isActive ? 700 : 500, color: isActive ? '#0176d3' : '#181818' }}>
                                    {label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {status.message && (
                    <Alert severity={status.type} sx={{ mb: 3, borderRadius: '4px', borderLeft: `4px solid ${status.type === 'success' ? '#2e7d32' : '#d32f2f'}` }}>
                        {status.message}
                    </Alert>
                )}

                {/* FORM CONTROLLER WORKSPACE */}
                <Paper variant="outlined" sx={{ p: 4, bgcolor: '#ffffff', borderColor: '#c9c9c9', borderRadius: '4px' }}>

                    {/* STAGE 1: IDENTITY */}
                    {activeStep === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                                <BadgeOutlined sx={{ color: '#0176d3' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Identity & Communication Parameters</Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth required label="System Employee ID" name="employee_id" value={formData.employee_id} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={8}><TextField size="small" fullWidth required label="Full Legal Name" name="full_name" value={formData.full_name} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth required type="email" label="Corporate Email Address" name="email" value={formData.email} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Contact Number" name="phone_number" value={formData.phone_number} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth type="date" label="Date of Birth" name="dob" value={formData.dob} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>

                                {/* FILE UPLOADER COMPONENT LINK GENERATOR */}
                                <Grid item xs={12} sm={8}>
                                    <Box sx={{ border: '1px dashed #c9c9c9', p: 1, borderRadius: '4px', display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fafafa' }}>
                                        <Button variant="outlined" component="label" size="small" startIcon={<CloudUploadOutlined />} sx={{ textTransform: 'none', color: '#515151', borderColor: '#c9c9c9' }}>
                                            Upload Profile Image
                                            <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                                        </Button>
                                        <Typography variant="caption" sx={{ color: '#706e6b' }}>
                                            {formData.profile_pic_url ? "Image processed into file link string" : "No file attached"}
                                        </Typography>
                                        {imagePreview && <Avatar src={imagePreview} sx={{ width: 32, height: 32, ml: 'auto' }} />}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Permanent Residential Address" name="home_address" value={formData.home_address} onChange={handleChange} /></Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* STAGE 2: OPERATIONS */}
                    {activeStep === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                                <BusinessCenterOutlined sx={{ color: '#0176d3' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Operational Assignment</Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Assigned Job Title" name="job_role" value={formData.job_role} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required size="small">
                                        <InputLabel id="system-role-label">System Access Role</InputLabel>
                                        <Select labelId="system-role-label" name="system_role" value={formData.system_role} label="System Access Role" onChange={handleChange}>
                                            <MenuItem value="Admin">Admin</MenuItem>
                                            <MenuItem value="HR">HR</MenuItem>
                                            <MenuItem value="BDE">BDE</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Reporting Manager" name="reporting_manager" value={formData.reporting_manager} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Office Base Location" name="office_location" value={formData.office_location} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth type="date" label="Date of Joining" name="date_of_joining" value={formData.date_of_joining} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth type="date" label="Date of Exit" name="date_of_exit" value={formData.date_of_exit} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth type="number" inputProps={{ step: "0.1" }} label="Prior Experience (Years)" name="previous_experience_years" value={formData.previous_experience_years} onChange={handleChange} /></Grid>
                                <Grid item xs={12}><TextField size="small" fullWidth label="Highest Educational Qualification" name="education_qualification" value={formData.education_qualification} onChange={handleChange} /></Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* STAGE 3: FINANCIALS */}
                    {activeStep === 2 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                                <AccountBalanceOutlined sx={{ color: '#0176d3' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Remittance Matrix & Infrastructure</Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="National ID (Aadhaar)" name="aadhar_number_encrypted" value={formData.aadhar_number_encrypted} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="PAN Number" name="pan_number" value={formData.pan_number} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Bank Name" name="bank_name" value={formData.bank_name} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Account Number" name="account_number" value={formData.account_number} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="IFSC Code" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth type="number" label="Gross CTC (Annual)" name="salary_details" value={formData.salary_details} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Corporate SIM Tracking" name="corp_sim_number" value={formData.corp_sim_number} onChange={handleChange} /></Grid>
                                <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Laptop Hardware Asset Tag" name="laptop_serial_number" value={formData.laptop_serial_number} onChange={handleChange} /></Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* STAGE 4: ADVANCED COMPREHENSIVE PREVIEW */}
                    {activeStep === 3 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                <AssignmentTurnedInOutlined sx={{ color: '#2e7d32' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>Registry Verification Preview</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#706e6b', mb: 3 }}>
                                Double check all mapped fields below. Press <strong>Confirm & Submit</strong> in the workspace header to finalize creation.
                            </Typography>

                            <Grid container spacing={3}>
                                {/* Live Profile Photo Widget */}
                                <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', borderRight: { md: '1px solid #e0e0e0' } }}>
                                    <Avatar src={imagePreview} sx={{ width: 120, height: 120, mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid #fff' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formData.full_name || 'Unspecified Name'}</Typography>
                                    <Typography variant="caption" sx={{ color: '#706e6b' }}>{formData.job_role || 'No Assigned Role'}</Typography>
                                </Grid>

                                {/* System Fields Breakdowns */}
                                <Grid item xs={12} md={9}>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '4px', borderColor: '#c9c9c9' }}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3', width: '30%' }}>Employee Identity</TableCell><TableCell>{formData.employee_id || '—'}</TableCell></TableRow>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3' }}>Primary Communication</TableCell><TableCell>{formData.email} {formData.phone_number ? `| ${formData.phone_number}` : ''}</TableCell></TableRow>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3' }}>System Permissions</TableCell><TableCell>{formData.system_role}</TableCell></TableRow>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3' }}>Reporting Chain</TableCell><TableCell>Reports to: {formData.reporting_manager || 'None Allocated'}</TableCell></TableRow>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3' }}>Operational Base</TableCell><TableCell>{formData.office_location || '—'} (Joined: {formData.date_of_joining || '—'})</TableCell></TableRow>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3' }}>Remittance Matrix</TableCell><TableCell>{formData.bank_name ? `${formData.bank_name} [Acc: ${formData.account_number}]` : '—'}</TableCell></TableRow>
                                                <TableRow><TableCell sx={{ fontWeight: 700, bgcolor: '#f3f3f3' }}>Allocated Hardware</TableCell><TableCell>{formData.laptop_serial_number || 'None Provisioned'}</TableCell></TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* BOTTOM WORKSPACE NAVIGATION */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button disabled={activeStep === 0} onClick={handleBack} sx={{ color: '#515151', fontWeight: 600, textTransform: 'none' }}>
                            Back
                        </Button>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: '#706e6b' }}>
                            Step {activeStep + 1} of {steps.length}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default AddEmployee;